<template>
  <div class="with-side-panel">
    <div class="content-i">
      <div class="content-box">
        <div class="element-wrapper">

          <BackButton
              :label="$t('general.button.back')"
              :to="{ name: 'officeList' }"
              marginBottom="5"/>

          <div class="d-flex justify-content-between">
            <h6 class="element-header">
              {{ $t('office.title') }}
            </h6>
          </div>

          <div class="element-box">
            <form v-if="isLoaded" @submit.prevent="submitForm" novalidate>
              <div class="row">
                <div class="col-lg-6">
                  <BaseInput
                      v-model="formData.name"
                      label="Bezeichnung"
                      :disabled="formData.hubspotId"
                      :required=true
                      :validation="v$.name"
                      :messages="v$.name.required.$message" />
                  <BaseInput
                      v-model="formData.address"
                      label="Straße"
                      :disabled="formData.hubspotId"
                      :required=true
                      :validation="v$.address"
                      :messages="v$.address.required.$message" />
                  <div class="row">
                    <div class="col-6">
                      <BaseInput
                          v-model="formData.houseNumber"
                          label="Hausnummer"
                          :disabled="formData.hubspotId"
                          :required=true
                          :validation="v$.houseNumber"
                          :messages="v$.houseNumber.required.$message" />
                    </div>
                    <div class="col-6">
                      <BaseInput
                          v-model="formData.zip"
                          label="PLZ"
                          :disabled="formData.hubspotId"
                          :required=true
                          :validation="v$.zip"
                          :messages="v$.zip.required.$message" />
                    </div>
                  </div>
                  <div v-if="officeEngagementTypesDropdown && officeEngagementTypesDropdown.length">
                    <SelectedDropdown
                        v-model="formData.engagementType"
                        :options="officeEngagementTypesDropdown"
                        :disabled="formData.hubspotId"
                        :required="true"
                        :label="$t('general.office_engagement_type')"
                        :placeholder="$t('placeholder.engagement_type')"
                        :validation="v$.engagementType"
                        :messages="v$.engagementType.required.$message"/>
                  </div>
                  <div v-if="officeTypesDropdown && officeTypesDropdown.length">
                    <SelectedDropdown
                        v-model="formData.type"
                        :options="officeTypesDropdown"
                        :disabled="formData.hubspotId"
                        :required="true"
                        :label="$t('general.type')"
                        :placeholder="$t('placeholder.type')"
                        :validation="v$.type"
                        :messages="v$.type.required.$message"/>
                  </div>
                  <BaseInput
                      v-model="formData.colorCode"
                      label="Standortfarbe"
                      :required=true
                      style="width: 100px; float: left"
                      type="color"
                      :inputStyle="{
                               width: '50px',
                               backgroundColor: '#f0f0f0',
                               padding: '1px',
                             }">
                  </BaseInput>
                </div>
                <div class="col-lg-6">
                  <BaseInput
                      v-model="formData.city"
                      label="Stadt"
                      :disabled="formData.hubspotId"
                      :required=true
                      :validation="v$.city"
                      :messages="v$.city.required.$message" />
                  <BaseInput
                      v-model="formData.phone"
                      :disabled="formData.hubspotId"
                      label="Telefonnummer" />
                  <BaseInput
                      v-model="formData.email"
                      label="Email"
                      type="email"
                      :disabled="formData.hubspotId"
                      :validation="v$.email"
                      :messages="v$.email.isValidEmail.$message" />
                  <div v-if="federalStatesDropdown && federalStatesDropdown.length">
                    <SelectedDropdown
                        v-model="formData.federalState"
                        @update:modelValue="updateFederalState"
                        :options="federalStatesDropdown"
                        :required="false"
                        :disabled="formData.hubspotId"
                        :label="$t('general.federal_state')"
                        :placeholder="$t('placeholder.select_federal_state')"
                        :validation="v$.federalState"
                        :messages="v$.federalState.required.$message"/>
                  </div>
                  <BaseInput
                      v-model="formData.regionTranslation"
                      label="Region"
                      :required=true
                      :validation="v$.region"
                      :disabled="formData.hubspotId"
                      :messages="v$.region.required.$message"
                      readonly />
                  <BaseInput
                      v-model="formData.costCenter"
                      label="KS1"
                      :required=true
                      :validation="v$.costCenter"
                      :messages="v$.costCenter.required.$message" />
                </div>
              </div>
              <div class="form-buttons-w text-center">
                <SaveButton
                    :label="$t('general.button.save')"
                    :loading="isSubmitting"/>
              </div>
            </form>
          </div>
        </div>
      </div>
      <Sidebar></Sidebar>
    </div>
  </div>
</template>

<script setup lang="ts">
import officeService from '@/api/Office';
import BaseInput from '@/components/base/BaseInput.vue';
import CreateButton from '@/components/CreateButton.vue';
import Sidebar from '@/components/FormSidebar.vue';
import SelectedDropdown from '@/components/SelectDropdown.vue';
import {ErrorToast, SuccessToast} from '@/components/VueToast';
import router from '@/router';
import {isValidEmail, requiredRule} from '@/shared/validators/validationRules';
import {piniaEnumsStore} from '@/store/piniaEnumsStore';
import useVuelidate from '@vuelidate/core';
import {computed, onMounted, ref, reactive} from 'vue';
import {useI18n} from 'vue-i18n';
import {useRoute} from 'vue-router';
import {IOffice, IRegion, IRegionFromFederalState} from "@/interfaces/Office/IOffice";
import BackButton from "@/components/button/BackButton.vue";
import SaveButton from "@/components/button/SaveButton.vue";

const {t} = useI18n();
const piniaEnumsState = piniaEnumsStore();
const getEnums = computed(() => piniaEnumsState.enums);
const federalStatesDropdown = ref([]);
const officeEngagementTypesDropdown = ref([]);
const officeTypesDropdown = ref([]);
const regionsDropdown = ref<IRegion[]>([]);
const isSubmitting = ref(false);
const isLoaded = ref(false);
const route = useRoute();
const officeId = route.params.id;

const formData = reactive<IOffice>({
  name: '',
  address: '',
  houseNumber: '',
  zip: '',
  engagementType: '',
  colorCode: '#43ad4e',
  city: '',
  phone: '',
  email: '',
  type: '',
  federalState: '',
  region: '',
  regionTranslation: '',
  costCenter: '',
});

const rules = computed(() => {
  return {
    name: requiredRule(t),
    address: requiredRule(t),
    houseNumber: requiredRule(t),
    zip: requiredRule(t),
    engagementType: requiredRule(t),
    type: requiredRule(t),
    city: requiredRule(t),
    federalState: requiredRule(t),
    region: requiredRule(t),
    costCenter: requiredRule(t),
    email: isValidEmail(t),
  };
});

const v$ = useVuelidate<IOffice>(rules, formData);

async function submitForm() {
  v$.value.$touch();
  if (v$.value.$invalid) {
    return;
  }

  if (isSubmitting.value) return;
  isSubmitting.value = true;

  try {
    if (officeId) {
      await officeService.putOffice(formData, +officeId);
    } else {
      await officeService.postOffice(formData);
    }

    await router.push({name: 'officeList'});
    isSubmitting.value = false;
    SuccessToast(t('general.toasts.save_success'));
  } catch (error) {
    await ErrorToast(error);
  } finally {
    isSubmitting.value = false;
  }
}

async function getOffice() {
  try {
    const {data} = await officeService.getOffice(+officeId);
    const regionTranslation = data.data.regionTranslation

    Object.assign(formData, {
      ...data.data,
      regionTranslation: data.data.regionTranslation
    });
  } catch (error) {
    await ErrorToast(error);
  }
}

const updateFederalState = (newValue: string) => {
  // const regionFromFederalState = regionsDropdown.value.find(
  //     regionDropdown => regionDropdown.federal_state_key === newValue
  // );

  const regionFromFederalState = regionsDropdown.value.find(
      regionDropdown => regionDropdown.federal_state_key === newValue
  ) as IRegionFromFederalState | undefined;

  if (regionFromFederalState) {
    formData.region = regionFromFederalState.key;
    formData.regionTranslation = regionFromFederalState.value;
  } else {
    formData.region = '';
    formData.regionTranslation = '';
  }
};

async function getOfficeEnumDropdowns() {
  federalStatesDropdown.value = getEnums.value?.federalStates || [];
  officeTypesDropdown.value = getEnums.value?.officeTypes || [];
  officeEngagementTypesDropdown.value = getEnums.value?.officeEngagementTypes || [];
  regionsDropdown.value = getEnums.value?.regions || [];
}

onMounted(async () => {
  //if (Object.keys(getEnums.value).length === 0) {
  //  await store.dispatch('getEnums', { t });
  //}

  await getOfficeEnumDropdowns();
  if (officeId) {
    await getOffice();
  }

  isLoaded.value = true;
});

</script>

<style scoped>
</style>